"use client";

import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

interface ComparisonTableProps {
  title?: string;
  subtitle?: string;
  items: Array<{ feature: string; us: string; others: string }>;
  accentColor?: string;
}

export function ComparisonTable({
  title,
  subtitle,
  items,
  accentColor = "#2C78B9",
}: ComparisonTableProps) {
  return (
    <section className="py-16 md:py-24 bg-slate-50/50 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(44,120,185,0.05),transparent_70%)] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {(title || subtitle) && (
          <div className="text-center mb-16">
            {title && (
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4"
              >
                {title}
              </motion.h2>
            )}
            {subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed font-medium"
              >
                {subtitle}
              </motion.p>
            )}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.05)] border border-slate-100 overflow-hidden"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 bg-[#0F172A] p-4 md:p-6 text-white sticky top-0 z-20">
            <div className="hidden md:block text-xs md:text-sm font-black uppercase tracking-widest text-white">
              Feature
            </div>
            <div className="text-center text-[10px] md:text-sm font-black uppercase tracking-widest text-white">
              Us
            </div>
            <div className="text-center text-[10px] md:text-sm font-black uppercase tracking-widest text-white">
              Others
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {items.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05, duration: 0.4, ease: "easeOut" }}
                className="grid grid-cols-2 md:grid-cols-3 p-5 md:p-6 items-center hover:bg-slate-50/50 transition-colors group gap-y-4 md:gap-y-0"
              >
                <div className="col-span-2 md:col-span-1 text-sm md:text-[15px] font-bold text-slate-800 tracking-tight border-b border-slate-50 pb-2 md:pb-0 md:border-0">
                  <span className="md:hidden text-[10px] text-slate-400 uppercase tracking-wider block mb-1">
                    Feature
                  </span>
                  {item.feature}
                </div>

                <div className="flex flex-col items-center gap-1 md:border-l md:border-slate-50">
                  <div className="flex items-center gap-2" style={{ color: accentColor }}>
                    <Check size={16} strokeWidth={3} className="shrink-0 md:w-[18px]" />
                    <span className="text-xs md:text-[15px] font-black tracking-tight">{item.us}</span>
                  </div>
                  <div className="h-0.5 w-0 group-hover:w-1/2 transition-all duration-500" style={{ backgroundColor: `${accentColor}33` }} />
                </div>

                <div className="flex items-center justify-center gap-2 text-slate-400 md:border-l md:border-slate-50">
                  <X size={16} strokeWidth={2} className="shrink-0 opacity-40 md:w-[18px]" />
                  <span className="text-xs md:text-[15px] font-semibold tracking-tight">{item.others}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-blue-50/30 p-6 text-center border-t border-slate-50">
            <div className="text-base md:text-lg font-black text-slate-900">
              Built for <span style={{ color: accentColor }}>Speed</span>. Engineered for{" "}
              <span className="text-[#059A8F]">Success</span>.
            </div>
          </div>
        </motion.div>

        <div className="mt-8 text-center lg:hidden">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Scroll to see full comparison
          </p>
        </div>
      </div>
    </section>
  );
}
